import type { PbxSettingsLocale } from './en'

export const pbxSettingsEs = {
  loading: 'Cargando configuración de PBX...',
  invalidPbx: {
    title: 'PBX inválido',
    description:
      'Se requiere un ID de PBX válido para editar la configuración del PBX. Seleccione un PBX desde el panel.',
  },
  loadError: {
    title: 'No se pudo cargar la configuración del PBX',
    description: 'Inténtelo de nuevo.',
  },
  partialLoadWarning: {
    title: 'Algunos datos no se pudieron cargar',
    unknownError: 'Error desconocido',
    labels: {
      providerGateways: 'Gateways de proveedor',
      serverGroups: 'Grupos de servidores',
      phoneNumbers: 'Números de teléfono',
      virtualFaxes: 'Faxes virtuales',
      alert911Options: 'Opciones 911',
    },
  },
  actions: {
    update: 'Actualizar',
  },
  common: {
    loading: 'Cargando...',
    select: 'Seleccionar',
  },
  sections: {
    core: {
      title: 'Configuración de PBX',
    },
    advanced: {
      title: 'Configuración avanzada de PBX',
    },
    system: {
      title: 'Configuración del sistema',
    },
  },
  placeholders: {
    selectFromList: 'Seleccionar de la lista...',
    selectServerGroup: 'Seleccionar grupo de servidores',
    selectTimeZone: 'Seleccionar zona horaria',
    selectExtensionDigits: 'Seleccionar dígitos de extensión',
    selectCodec: 'Seleccionar códec',
  },
  fields: {
    pbxDisplayName: {
      label: 'Nombre visible del PBX',
    },
    company: {
      label: 'Empresa',
    },
    providerGateway: {
      label: 'Gateway de proveedor',
    },
    emergencyProviderGateway: {
      label: 'Gateway de proveedor de emergencia',
    },
    internationalProviderGateway: {
      label: 'Gateway de proveedor internacional',
    },
    active: {
      label: 'Activo',
    },
    smsSenderName: {
      label: 'Nombre del remitente de SMS',
      copyFromCompany: 'Copiar de la Empresa',
      helperText:
        'Se muestra a sus clientes en las respuestas de cancelación de SMS, p. ej. "{{name}}: Ha cancelado su suscripción correctamente." Esto es por ubicación: edítelo aquí si difiere del nombre de la empresa.',
      divergenceHint:
        'Difiere del nombre de la empresa "{{companyName}}"; esto es lo esperado para clientes con varias ubicaciones.',
    },
    serverGroup: {
      label: 'Grupo de servidores',
    },
    advanced: {
      maximum_allowed_channels: 'Canales máximos',
      maximum_lines_in: 'Canales entrantes',
      maximum_lines_out: 'Canales por extensión',
      call_log_retention_period_in_months: 'Historial de llamadas (en meses)',
    },
    industry: {
      label: 'Industria',
    },
    ehr: {
      label: 'EHR',
      helperText: 'Solo para fines de reportes, no afecta la funcionalidad',
    },
    defaultEmergencyAlert: {
      label: 'Alerta de emergencia predeterminada',
    },
    defaultTimeZone: {
      label: 'Zona horaria predeterminada',
      britishColumbia: 'Hora del Pacífico - Columbia Británica (PCT)',
    },
    callParkTimeout: {
      label: 'Tiempo de espera de estacionamiento',
    },
    extensionDigits: {
      label: 'Dígitos de extensión',
    },
    defaultAreaCode: {
      label: 'Código de área predeterminado',
    },
    defaultCallerId: {
      label: 'Identificador de llamadas predeterminado',
    },
    defaultFaxNumber: {
      label: 'Número de fax predeterminado',
    },
    defaultVirtualFax: {
      label: 'Fax virtual predeterminado',
    },
    preferredCodec: {
      label: 'Códec preferido',
    },
    checkboxes: {
      enable_inbound_recording: 'Grabación de llamadas entrantes',
      enable_outbound_recording: 'Grabación de llamadas salientes',
      enable_internal_recording: 'Grabación de llamadas internas',
      international: 'Internacional',
      enable_vm_transcription: 'Transcripción de buzón de voz',
      schedules_v2: 'Horario V2',
      enable_agent_statuses: 'Centro de llamadas',
      enable_call_analytics: 'Mango AI',
      is_grove: 'Grove',
    },
  },
  companySelect: {
    placeholder: 'Seleccionar empresa',
    ariaLabel: 'Selector de empresa',
    searchPlaceholder: 'Buscar empresas...',
    empty: {
      loading: 'Cargando empresas...',
      noResults: 'No se encontraron empresas',
      typeToSearch: 'Escriba para buscar empresas',
    },
  },
  phoneSelect: {
    placeholder: 'Seleccionar de la lista...',
    ariaLabel: 'Selector de número de teléfono',
    searchPlaceholder: 'Buscar números de teléfono...',
    empty: {
      loading: 'Cargando números de teléfono...',
      noResults: 'No se encontraron números de teléfono',
      noData: 'No hay números de teléfono disponibles',
    },
  },
  toast: {
    validationError: 'Error de validación',
    timezoneChanged: {
      title: 'Zona horaria cambiada',
      description:
        'Cambió la zona horaria del PBX. Recuerde actualizar sus horarios en consecuencia.',
    },
    noChanges: 'No hay cambios para guardar',
    saveSuccess: 'Configuración del PBX actualizada',
    saveError: {
      title: 'Error en la actualización',
      description: 'Hubo un problema al actualizar la configuración del PBX.',
    },
  },
  callParkConfirm: {
    title: 'Confirmar tiempo de estacionamiento de llamadas',
    description:
      '¿Está seguro de que desea mantener a los llamantes en el estacionamiento por más de 10 minutos?',
    cancel: 'Restablecer tiempo',
    confirm: 'Continuar',
  },
  rebuildAndReboot: {
    title: 'Configuración del PBX actualizada',
    description:
      'Es posible que algunas configuraciones del PBX no surtan efecto en los dispositivos hasta que se reconstruyan y se reinicien.',
    cancel: 'Ahora no',
    confirm: 'Reconstruir y reiniciar dispositivos',
    toast: {
      successTitle: 'Reconstrucción y reinicio iniciados',
      successDescription:
        'Todos los dispositivos se están reconstruyendo y reiniciando.',
      errorTitle: 'Error al reconstruir y reiniciar',
      errorDescription:
        'Hubo un problema al reconstruir y reiniciar los dispositivos.',
    },
  },
  validation: {
    pbxDisplayNameRequired: 'Por favor, ingrese un Nombre visible del PBX.',
    companyRequired: 'Por favor, seleccione una Empresa.',
    smsSenderNameRequired: 'Por favor, ingrese un Nombre del remitente de SMS.',
    serverGroupRequired: 'Por favor, seleccione un Grupo de servidores.',
    primaryOutboundGatewayRequired:
      'Por favor, seleccione un Gateway de salida principal.',
    primaryInternationalGatewayRequired:
      'Por favor, seleccione un Gateway internacional principal.',
    emergencyGatewayRequired: 'Por favor, seleccione un Gateway de emergencia.',
    industryRequired: 'Por favor, seleccione una Industria.',
    maxChannelsRequired: 'Por favor, ingrese Canales máximos.',
    inboundChannelsRequired: 'Por favor, ingrese Canales entrantes.',
    perExtensionChannelsRequired: 'Por favor, ingrese Canales por extensión.',
    callHistoryRequired: 'Por favor, ingrese Historial de llamadas.',
    numericFieldRange: '{{field}} debe estar entre {{min}} y {{max}}.',
    numericFieldMin: 'El mínimo es {{min}}.',
    numericFieldMax: 'El máximo es {{max}}.',
    defaultTimeZoneRequired:
      'Por favor, seleccione una Zona horaria predeterminada.',
    callParkTimeoutRange:
      'Por favor, ingrese un Tiempo de espera de estacionamiento entre {{min}}-{{max}} (Recomendado: {{recommended}}).',
    callParkTimeoutMax:
      'El Tiempo de espera de estacionamiento no puede exceder {{max}} segundos.',
    extensionDigitsRequired: 'Por favor, ingrese los Dígitos de extensión.',
    extensionDigitsInvalid: 'Los Dígitos de extensión deben ser 3, 4 o 5.',
    defaultAreaCodeRequired:
      'Por favor, ingrese el Código de área predeterminado.',
    defaultAreaCodeDigits:
      'El Código de área predeterminado debe ser un número de 3 dígitos.',
    defaultAreaCodeRange:
      'El Código de área predeterminado debe estar entre 200 y 998.',
    defaultAreaCodeDisallowed:
      'El código de área {{code}} no está permitido para el Código de área predeterminado.',
    defaultCallerIdRequired:
      'Por favor, seleccione un Identificador de llamadas predeterminado.',
    defaultFaxNumberRequired:
      'Por favor, seleccione un Número de fax predeterminado.',
    defaultVirtualFaxRequired:
      'Por favor, seleccione un Fax virtual predeterminado.',
    preferredCodecRequired: 'Por favor, seleccione un Códec preferido.',
  },
} satisfies PbxSettingsLocale
